import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiPackage, FiAlertTriangle, FiTrendingUp,
  FiShoppingCart, FiRefreshCw, FiSearch, FiPlusCircle,
  FiBarChart2, FiClipboard, FiBox, FiBell,
  FiChevronRight, FiX, FiCheckCircle, FiActivity,
  FiZap, FiFilter,
} from 'react-icons/fi';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Area, AreaChart,
} from 'recharts';
import '../Styles/Inventory.css';
import * as inventoryApi from '../utils/inventoryApi';

const TABS = [
  { id: 'dashboard',   label: 'Dashboard',        icon: <FiBarChart2 size={14} /> },
  { id: 'stock',       label: 'Stock Predict',     icon: <FiBox size={14} /> },
  { id: 'procurement', label: 'Procurement',       icon: <FiShoppingCart size={14} /> },
  { id: 'orders',      label: 'Purchase Orders',   icon: <FiClipboard size={14} /> },
  { id: 'manual',      label: 'Manual',            icon: <FiPlusCircle size={14} /> },
];

const getBadgeClass = (value) => {
  const map = {
    SAFE: 'inv-badge-ok', OK: 'inv-badge-ok', CREATED: 'inv-badge-created',
    LOCAL_ONLY: 'inv-badge-warning', LOW: 'inv-badge-warning',
    WARNING: 'inv-badge-warning', MEDIUM: 'inv-badge-warning',
    CRITICAL: 'inv-badge-critical', HIGH: 'inv-badge-critical',
    A: 'inv-badge-ok', B: 'inv-badge-ok',
    C: 'inv-badge-warning', D: 'inv-badge-critical', F: 'inv-badge-critical',
  };
  return map[value] || 'inv-badge-default';
};

const SAFE_STATUSES = new Set(['SAFE', 'OK']);
const WARN_STATUSES = new Set(['WARNING', 'MEDIUM']);
const CRIT_STATUSES = new Set(['CRITICAL', 'HIGH']);

const getHealthColor = (s) => s >= 70 ? '#15803d' : s >= 40 ? '#b45309' : '#dc2626';
const getHealthGradient = (s) => s >= 70
  ? 'linear-gradient(90deg,#15803d,#16a34a)'
  : s >= 40
  ? 'linear-gradient(90deg,#b45309,#d97706)'
  : 'linear-gradient(90deg,#dc2626,#ef4444)';

const getStatusBoxClass = (status) => {
  if (CRIT_STATUSES.has(status)) return 'inv-box-danger';
  if (WARN_STATUSES.has(status)) return 'inv-box-warning';
  return 'inv-box-success';
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? d : new Intl.DateTimeFormat(navigator.language, { day: '2-digit', month: 'short' }).format(dt);
};

const fmtFullDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? d : new Intl.DateTimeFormat(navigator.language).format(dt);
};

export default function Inventory() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  // ── shared data ──
  const [alerts, setAlerts]           = useState([]);
  const [procurement, setProcurement] = useState([]);
  const [purchaseOrders, setPOs]      = useState([]);

  // ── stock prediction tab ──
  const [materialId, setMaterialId]   = useState('2040150038');
  const [plant, setPlant]             = useState('1301');
  const [stockData, setStockData]     = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [forecastWeeks, setForecastWeeks] = useState(12);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [searching, setSearching]     = useState(false);
  const [materials, setMaterials]     = useState([]);

  // ── alerts tab ──
  const [alertFilter, setAlertFilter]         = useState('ALL');
  const [alertPlantInput, setAlertPlantInput] = useState('');
  const [alertPlantFilter, setAlertPlantFilter] = useState('');
  const [alertDrawerOpen, setAlertDrawerOpen] = useState(false);
  const [alertDrawerItem, setAlertDrawerItem] = useState(null);
  const [alertDrawerStock, setAlertDrawerStock]     = useState(null);
  const [alertDrawerForecast, setAlertDrawerForecast] = useState(null);
  const [alertDrawerLoading, setAlertDrawerLoading] = useState(false);

  // ── procurement tab ──
  const [selectedItem, setSelectedItem]         = useState(null);
  const [sidebarOpen, setSidebarOpen]           = useState(false);
  const [sidebarStock, setSidebarStock]         = useState(null);
  const [sidebarLoading, setSidebarLoading]     = useState(false);
  const [showReview, setShowReview]             = useState(false);
  const [reviewVendor, setReviewVendor]         = useState('600000');
  const [reviewQty, setReviewQty]               = useState('');
  const [creatingManualPO, setCreatingManualPO] = useState(false);
  const [poResult, setPoResult]                 = useState(null);
  const [showAutoConfirm, setShowAutoConfirm]   = useState(false);
  const [creatingAutoPO, setCreatingAutoPO]     = useState(false);
  // manual PO form
  const [manualMat, setManualMat]       = useState('');
  const [manualPlant, setManualPlant]   = useState('1000');
  const [manualVendor, setManualVendor] = useState('600000');
  const [manualQty, setManualQty]       = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [selectedRows, setSelectedRows]   = useState(new Set());
  const [bulkCreating, setBulkCreating]   = useState(false);
  const [bulkProgress, setBulkProgress]   = useState({ done: 0, total: 0 });

  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((msg, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [alertData, procData, poData] = await Promise.all([
        inventoryApi.getAlerts({ plant: alertPlantFilter || undefined }),
        inventoryApi.getAutoProcurement(),
        inventoryApi.getPurchaseOrders(),
      ]);
      setAlerts(alertData);
      setProcurement(procData);
      setPOs(poData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [alertPlantFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    inventoryApi.getMaterials()
      .then(data => setMaterials(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // ── stock search ──
  const handleSearch = async () => {
    if (!materialId.trim() || !plant.trim()) return;
    setSearching(true);
    setError(null);
    setStockData(null);
    setForecastData(null);
    try {
      const [stock, forecast] = await Promise.all([
        inventoryApi.getStockPrediction(materialId, plant),
        inventoryApi.getForecastDemand(materialId, forecastWeeks),
      ]);
      if (stock.error) throw new Error(stock.error);
      setStockData(stock);
      setForecastData(forecast);
    } catch (err) {
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  // ── alerts drawer ──
  const openAlertDrawer = async (alert) => {
    setAlertDrawerItem(alert);
    setAlertDrawerOpen(true);
    setAlertDrawerStock(null);
    setAlertDrawerForecast(null);
    setAlertDrawerLoading(true);
    try {
      const [stock, forecast] = await Promise.all([
        inventoryApi.getStockPrediction(alert.material_id, alert.plant),
        inventoryApi.getForecastDemand(alert.material_id, forecastWeeks),
      ]);
      setAlertDrawerStock(stock);
      setAlertDrawerForecast(forecast);
    } catch (_) {}
    finally { setAlertDrawerLoading(false); }
  };

  const closeAlertDrawer = () => {
    setAlertDrawerOpen(false);
    setAlertDrawerItem(null);
  };

  // ── procurement sidebar ──
  const openSidebar = async (item) => {
    setSelectedItem(item);
    setSidebarOpen(true);
    setSidebarStock(null);
    setPoResult(null);
    setShowReview(false);
    setSidebarLoading(true);
    try {
      const stock = await inventoryApi.getStockPrediction(item.material_id, item.plant);
      setSidebarStock(stock);
    } catch (_) {}
    finally { setSidebarLoading(false); }
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setShowReview(false);
    setPoResult(null);
  };

  const openReview = () => {
    setReviewVendor('600000');
    setReviewQty(String(selectedItem.reorder_quantity ?? ''));
    setPoResult(null);
    setShowReview(true);
  };

  const handleCreateManualPO = async () => {
    setCreatingManualPO(true);
    try {
      const result = await inventoryApi.createPOManual(
        selectedItem.material_id, selectedItem.plant, reviewVendor, Number(reviewQty),
      );
      setPoResult(result);
      showToast(`PO ${result.po_number} — ${result.status}`);
      const poData = await inventoryApi.getPurchaseOrders();
      setPOs(poData);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setCreatingManualPO(false);
    }
  };

  // ── auto-create all HIGH POs ──
  const handleAutoCreateAll = async () => {
    setCreatingAutoPO(true);
    setShowAutoConfirm(false);
    try {
      const newPOs = await inventoryApi.createPurchaseOrder();
      const poData = await inventoryApi.getPurchaseOrders();
      setPOs(poData);
      showToast(`${newPOs.length} PO(s) created for HIGH priority materials`);
      setActiveTab('orders');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setCreatingAutoPO(false);
    }
  };

  // ── manual PO form ──
  const handleManualFormSubmit = async (e) => {
    e.preventDefault();
    if (!manualMat.trim() || !manualPlant.trim() || !manualVendor.trim() || !manualQty) return;
    setManualLoading(true);
    try {
      const result = await inventoryApi.createPOManual(manualMat, manualPlant, manualVendor, Number(manualQty));
      showToast(`PO ${result.po_number} created — ${result.status}`);
      setManualMat(''); setManualQty('');
      const poData = await inventoryApi.getPurchaseOrders();
      setPOs(poData);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setManualLoading(false);
    }
  };

  // ── forecast weeks ──
  const handleWeeksChange = async (weeks) => {
    setForecastWeeks(weeks);
    if (!stockData) return;
    setForecastLoading(true);
    try {
      const forecast = await inventoryApi.getForecastDemand(materialId, weeks);
      setForecastData(forecast);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setForecastLoading(false);
    }
  };

  // ── procurement row selection ──
  const toggleRow = (idx) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedRows(prev =>
      prev.size === procurement.length
        ? new Set()
        : new Set(procurement.map((_, i) => i))
    );
  };

  const handleBulkCreatePOs = async () => {
    const items = [...selectedRows].map(idx => procurement[idx]).filter(Boolean);
    if (!items.length) return;
    setBulkCreating(true);
    setBulkProgress({ done: 0, total: items.length });
    let successCount = 0;
    for (const item of items) {
      try {
        await inventoryApi.createPOManual(
          item.material_id, item.plant, item.vendor || '600000', item.reorder_quantity,
        );
        successCount++;
      } catch (_) {}
      setBulkProgress(prev => ({ ...prev, done: prev.done + 1 }));
    }
    setBulkCreating(false);
    setSelectedRows(new Set());
    try {
      const poData = await inventoryApi.getPurchaseOrders();
      setPOs(poData);
    } catch (_) {}
    showToast(`${successCount} of ${items.length} PO(s) created`);
    if (successCount > 0) setActiveTab('orders');
  };

  // ── derived ──
  const highRiskCount   = alerts.filter(a => a.risk === 'HIGH').length;
  const mediumRiskCount = alerts.filter(a => a.risk === 'MEDIUM').length;
  const critMaterials   = alerts.filter(a => Number(a.days_left) < 7).length;
  const todayStr        = new Date().toISOString().slice(0, 10);
  const posToday        = purchaseOrders.filter(p => p.po_date === todayStr).length;

  const filteredAlerts = alertFilter === 'ALL' ? alerts
    : alerts.filter(a => a.risk === alertFilter);

  // build recharts series from forecast
  const forecastChartData = forecastData?.forecast?.map(f => ({
    week: fmtDate(f.week_start),
    demand: parseFloat(f.predicted_demand?.toFixed(1)),
    upper:  parseFloat(f.upper_bound?.toFixed(1)),
    lower:  parseFloat(f.lower_bound?.toFixed(1)),
  })) || [];

  const alertForecastData = alertDrawerForecast?.forecast?.map(f => ({
    week: fmtDate(f.week_start),
    demand: parseFloat(f.predicted_demand?.toFixed(1)),
    upper:  parseFloat(f.upper_bound?.toFixed(1)),
    lower:  parseFloat(f.lower_bound?.toFixed(1)),
  })) || [];

  return (
    <div className="inv-app">

      {/* ── Toast ── */}
      <div className="inv-toast-wrap" aria-live="polite">
        {toasts.map(t => (
          <div key={t.id} className={`inv-toast inv-toast-${t.type}`}>
            <span className="inv-toast-icon">{t.type === 'success' ? '✓' : t.type === 'error' ? '✗' : 'ℹ'}</span>
            {t.msg}
          </div>
        ))}
      </div>

      {/* ── Ambient orbs ── */}
      <div className="inv-bg-orbs" aria-hidden="true">
        <span className="inv-bg-orb inv-bg-orb-1" />
        <span className="inv-bg-orb inv-bg-orb-2" />
        <span className="inv-bg-orb inv-bg-orb-3" />
      </div>

      {/* ── Topbar ── */}
      <div className="inv-topbar">
        <button className="inv-topbar-back" onClick={() => navigate('/home')}>
          <FiArrowLeft size={14} /> Back
        </button>
        <span className="inv-topbar-title">Inventory Optimization</span>
        <span className="inv-live-badge"><span className="inv-live-dot" />Live</span>
        <button
          className="inv-btn inv-btn-ghost"
          style={{ fontSize: 12 }}
          onClick={() => { fetchData(); showToast('Data refreshed'); }}
          disabled={loading}
        >
          <FiRefreshCw size={13} className={loading ? 'inv-spinner' : ''} />
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="inv-alert-err"><FiAlertTriangle size={14} /> {error}</div>
      )}

      {/* ── Tabs ── */}
      <div className="inv-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`inv-tab-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="inv-content">
        <div key={activeTab} className="inv-tab-panel">

          {/* ════ DASHBOARD ════ */}
          {activeTab === 'dashboard' && (
            <>
              {/* KPI Cards */}
              <div className="inv-kpi-grid">
                <div className="inv-kpi inv-kpi-danger" style={{ '--delay': '0ms' }}>
                  <div className="inv-kpi-icon c2"><FiAlertTriangle /></div>
                  <div className="inv-kpi-body">
                    <span className="inv-kpi-label">HIGH Risk</span>
                    <span className="inv-kpi-value">{highRiskCount}</span>
                    <span className="inv-kpi-sub">immediate action needed</span>
                  </div>
                </div>
                <div className="inv-kpi" style={{ '--delay': '80ms' }}>
                  <div className="inv-kpi-icon c3"><FiBell /></div>
                  <div className="inv-kpi-body">
                    <span className="inv-kpi-label">MEDIUM Risk</span>
                    <span className="inv-kpi-value">{mediumRiskCount}</span>
                    <span className="inv-kpi-sub">plan soon</span>
                  </div>
                </div>
                <div className="inv-kpi" style={{ '--delay': '160ms' }}>
                  <div className="inv-kpi-icon c5"><FiActivity /></div>
                  <div className="inv-kpi-body">
                    <span className="inv-kpi-label">&lt; 7 Days Stock</span>
                    <span className="inv-kpi-value">{critMaterials}</span>
                    <span className="inv-kpi-sub">critical coverage</span>
                  </div>
                </div>
                <div className="inv-kpi" style={{ '--delay': '240ms' }}>
                  <div className="inv-kpi-icon c4"><FiClipboard /></div>
                  <div className="inv-kpi-body">
                    <span className="inv-kpi-label">POs Today</span>
                    <span className="inv-kpi-value">{posToday}</span>
                    <span className="inv-kpi-sub">created {todayStr}</span>
                  </div>
                </div>
              </div>

              {/* Risk Alerts */}
              <div className="inv-card">
                <div className="inv-panel-hd">
                  <div>
                    <h3>Risk Alerts</h3>
                    <small>
                      {filteredAlerts.length} material{filteredAlerts.length !== 1 ? 's' : ''} at risk
                      {alertPlantFilter && <span style={{ marginLeft: 6, fontSize: '0.78em', color: '#1976a0' }}>· Plant: {alertPlantFilter}</span>}
                    </small>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <select
                        value={alertPlantFilter}
                        onChange={e => { setAlertPlantInput(e.target.value); setAlertPlantFilter(e.target.value); }}
                        style={{
                          fontSize: 12, padding: '4px 8px', borderRadius: 6, width: 155, cursor: 'pointer',
                          border: `1px solid ${alertPlantFilter ? '#1976a0' : '#d8e3f0'}`,
                          background: alertPlantFilter ? '#e8f2ff' : '#f0f5fc', color: '#0f2147',
                        }}
                      >
                        <option value="">All Plants</option>
                        {['1124', '1134', '1202', '1301', '1111'].map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                    <div className="inv-filter-toggle">
                      {['ALL', 'HIGH', 'MEDIUM'].map(f => (
                        <button
                          key={f}
                          className={`inv-filter-btn${alertFilter === f ? ' active' : ''}${f === 'HIGH' ? ' danger' : f === 'MEDIUM' ? ' warn' : ''}`}
                          onClick={() => setAlertFilter(f)}
                        >
                          {f === 'HIGH' && <FiAlertTriangle size={11} />}
                          {f === 'MEDIUM' && <FiBell size={11} />}
                          {f === 'ALL' && <FiFilter size={11} />}
                          {f}
                          {f !== 'ALL' && (
                            <span className="inv-filter-count">
                              {alerts.filter(a => a.risk === f).length}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="inv-table-wrap" style={{ border: 'none', borderRadius: '0 0 var(--inv-r-xl) var(--inv-r-xl)' }}>
                  <table className="inv-table">
                    <thead>
                      <tr>
                        <th>Material Name</th>
                        <th>Material ID</th>
                        <th>Plant</th>
                        <th>Days Left</th>
                        <th>Risk</th>
                        <th>Risk Score</th>
                        <th>Expected Stockout</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAlerts.length === 0 ? (
                        <tr><td colSpan={8} className="inv-table-empty">
                          {loading ? 'Loading…' : '✅ All stock levels are healthy'}
                        </td></tr>
                      ) : filteredAlerts.map((alert, idx) => (
                        <tr
                          key={idx}
                          style={{ '--row-delay': `${idx * 40}ms` }}
                          className={alert.risk === 'HIGH' ? 'inv-row-high' : 'inv-row-medium'}
                          onClick={() => openAlertDrawer(alert)}
                        >
                          <td>{alert.material_description || '—'}</td>
                          <td><span className="inv-po-code">{alert.material_id}</span></td>
                          <td>{alert.plant}</td>
                          <td><strong style={{ color: Number(alert.days_left) <= 5 ? '#991b1b' : 'inherit' }}>{Number(alert.days_left).toFixed(1)}</strong> days</td>
                          <td><span className={`inv-badge ${getBadgeClass(alert.risk)}`}>{alert.risk}</span></td>
                          <td>{alert.risk_score ?? '—'}</td>
                          <td>{fmtFullDate(alert.expected_stockout_date)}</td>
                          <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={alert.reason}>{alert.reason || alert.action}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ════ STOCK PREDICTION ════ */}
          {activeTab === 'stock' && (
            <div className="inv-card">
              <div className="inv-panel-hd">
                <div>
                  <h3>Stock Prediction</h3>
                  <small>{forecastWeeks}-week demand forecast and inventory health</small>
                </div>
              </div>
              <div className="inv-card-body">
                <div className="inv-search-bar">
                  <div className="inv-field">
                    <label>Material ID</label>
                    <input
                      list="inv-mat-list"
                      type="text"
                      value={materialId}
                      onChange={e => setMaterialId(e.target.value)}
                      placeholder="e.g. MAT001"
                      onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    />
                    <datalist id="inv-mat-list">
                      {materials.map(m => (
                        <option key={m.material_id} value={m.material_id}>{m.material_description}</option>
                      ))}
                    </datalist>
                  </div>
                  <div className="inv-field">
                    <label>Plant</label>
                    <input
                      type="text"
                      value={plant}
                      onChange={e => setPlant(e.target.value)}
                      placeholder="e.g. 1000"
                      onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                  <button
                    className="inv-btn inv-btn-primary"
                    onClick={handleSearch}
                    disabled={searching}
                    style={{ height: 38, alignSelf: 'flex-end' }}
                  >
                    {searching
                      ? <><span className="inv-spinner" style={{ marginRight: 4 }} /> Searching…</>
                      : <><FiSearch size={13} /> Search</>}
                  </button>
                </div>

                {stockData && (
                  <>
                    {stockData.material_description && (
                      <div className="inv-material-header">
                        <span className="inv-material-id">{stockData.material_id}</span>
                        <strong>{stockData.material_description}</strong>
                        <span>· Plant {stockData.plant}</span>
                      </div>
                    )}

                    <div className="inv-stock-grid">
                      {[
                        { label: 'Current Stock',  val: stockData.current_stock,           unit: 'units',    delay: '0ms'   },
                        { label: 'Available Stock', val: stockData.available_stock,          unit: 'units',    delay: '40ms'  },
                        { label: 'Weekly Demand',  val: stockData.predicted_weekly_demand,  unit: 'units/wk', delay: '80ms'  },
                        { label: 'Days Left',      val: Number(stockData.days_left).toFixed(1), unit: 'days', delay: '120ms' },
                        { label: 'Status',         val: null, badge: stockData.status,                        delay: '160ms' },
                        { label: 'Health Grade',   val: null, badge: stockData.health_grade,
                          sub: `Score: ${stockData.health_score}/100`,                                         delay: '200ms' },
                      ].map((s, i) => (
                        <div className="inv-stock-stat" key={i} style={{ '--delay': s.delay }}>
                          <span className="inv-stock-stat-label">{s.label}</span>
                          {s.badge
                            ? <span className={`inv-badge ${getBadgeClass(s.badge)}`} style={{ fontSize: '1rem', height: 'auto', padding: '6px 16px' }}>{s.badge}</span>
                            : <span className="inv-stock-stat-val">{s.val}</span>}
                          {s.unit && <span className="inv-stock-stat-unit">{s.unit}</span>}
                          {s.sub  && <span className="inv-stock-stat-unit">{s.sub}</span>}
                        </div>
                      ))}
                    </div>

                    {stockData.health_score != null && (
                      <div className="inv-health-score-wrap">
                        <span className="inv-health-score-label">Health Score</span>
                        <span className="inv-health-score-num" style={{ color: getHealthColor(stockData.health_score) }}>
                          {stockData.health_score}/100
                        </span>
                        <div className="inv-health-score-bar">
                          <div className="inv-health-score-fill"
                            style={{ width: `${stockData.health_score}%`, background: getHealthGradient(stockData.health_score) }} />
                        </div>
                      </div>
                    )}

                    <div className="inv-metric-grid">
                      {[
                        { label: 'Safety Stock',        val: stockData.safety_stock },
                        { label: 'Reorder Point',       val: stockData.reorder_point },
                        { label: 'Demand Variability',  val: stockData.demand_variability },
                        { label: 'Stockout Probability',val: `${((stockData.stockout_probability ?? 0) * 100).toFixed(0)}%` },
                        { label: 'Expected Stockout',   val: fmtFullDate(stockData.expected_stockout_date) },
                      ].map((item, i) => (
                        <div key={i} className="inv-metric-tile">
                          <div className="inv-metric-label">{item.label}</div>
                          <div className="inv-metric-val">{item.val}</div>
                        </div>
                      ))}
                    </div>

                    {(CRIT_STATUSES.has(stockData.status) || WARN_STATUSES.has(stockData.status)) && (
                      <div className={`inv-info-box ${getStatusBoxClass(stockData.status)}`}>
                        <FiAlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <strong>{stockData.recommended_action}</strong>
                          <p>{Number(stockData.days_left).toFixed(1)} days of stock remaining at current demand rate.</p>
                        </div>
                      </div>
                    )}
                    {SAFE_STATUSES.has(stockData.status) && (
                      <div className="inv-info-box inv-box-success">
                        <FiTrendingUp size={16} style={{ flexShrink: 0 }} />
                        <strong>{stockData.recommended_action || `Stock levels healthy for ${stockData.material_id}`}</strong>
                      </div>
                    )}

                    {stockData.health_reasons?.length > 0 && (
                      <div className="inv-health-section">
                        <div className="inv-health-title">Health Analysis</div>
                        {stockData.health_reasons.map((reason, i) => (
                          <div key={i} className="inv-health-item">
                            <span className="inv-health-dot">●</span>{reason}
                          </div>
                        ))}
                      </div>
                    )}

                    {stockData.analysis && (
                      <div className="inv-ai-box">
                        <div className="inv-ai-title">AI Analysis</div>
                        <p className="inv-ai-text">{stockData.analysis}</p>
                      </div>
                    )}

                    {/* 12-week Forecast Line Chart */}
                    {forecastChartData.length > 0 && (
                      <div className="inv-chart-wrap">
                        <div className="inv-chart-title">
                          <FiTrendingUp size={14} />
                          {forecastWeeks}-Week Demand Forecast
                          {forecastData?.model_used && (
                            <span className="inv-chart-model">model: {forecastData.model_used}</span>
                          )}
                          {forecastData?.confidence_score != null && (
                            <span className="inv-chart-conf">confidence: {(forecastData.confidence_score * 100).toFixed(0)}%</span>
                          )}
                          {forecastLoading && <span className="inv-spinner" style={{ marginLeft: 8, width: 14, height: 14 }} />}
                          <select
                            value={forecastWeeks}
                            onChange={e => handleWeeksChange(Number(e.target.value))}
                            disabled={forecastLoading}
                            style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 6px', borderRadius: 6, border: '1px solid #d8e3f0', background: '#f0f5fc', color: '#0f2147', cursor: 'pointer' }}
                          >
                            {[4, 8, 12, 24].map(w => <option key={w} value={w}>{w} weeks</option>)}
                          </select>
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                          <AreaChart data={forecastChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="forecastBand" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="#1976a0" stopOpacity={0.15} />
                                <stop offset="95%" stopColor="#1976a0" stopOpacity={0.02} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#d8e3f0" />
                            <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#5b7293' }} />
                            <YAxis tick={{ fontSize: 11, fill: '#5b7293' }} />
                            <Tooltip
                              contentStyle={{ background: '#fff', border: '1px solid #d8e3f0', borderRadius: 8, fontSize: 12 }}
                              labelStyle={{ color: '#0f2147', fontWeight: 700 }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Area type="monotone" dataKey="upper" name="Upper Bound" stroke="transparent" fill="url(#forecastBand)" />
                            <Line type="monotone" dataKey="lower"  name="Lower Bound" stroke="#93b4e8" strokeWidth={1.5} dot={false} strokeDasharray="4 3" />
                            <Line type="monotone" dataKey="demand" name="Predicted Demand" stroke="#0f4c81" strokeWidth={2.5} dot={{ r: 3, fill: '#0f4c81' }} activeDot={{ r: 5 }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </>
                )}

                {!stockData && !searching && (
                  <div className="inv-empty">
                    <div className="inv-empty-icon">📦</div>
                    <strong>Enter search criteria</strong>
                    <p>Enter a Material ID and Plant, then press Search.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ════ PROCUREMENT ════ */}
          {activeTab === 'procurement' && (
            <>
              <div className="inv-card">
                <div className="inv-panel-hd">
                  <div>
                    <h3>Auto Procurement</h3>
                    <small>Click a row to review and create a PO</small>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {selectedRows.size > 0 && (
                      <button
                        className="inv-btn inv-btn-primary"
                        onClick={handleBulkCreatePOs}
                        disabled={bulkCreating}
                      >
                        {bulkCreating
                          ? <><span className="inv-spinner" style={{ marginRight: 4 }} /> Creating ({bulkProgress.done}/{bulkProgress.total})…</>
                          : <><FiCheckCircle size={13} /> Create {selectedRows.size} Selected PO{selectedRows.size !== 1 ? 's' : ''}</>}
                      </button>
                    )}
                    <button
                      className="inv-btn inv-btn-danger"
                      onClick={() => setShowAutoConfirm(true)}
                      disabled={creatingAutoPO || highRiskCount === 0}
                    >
                      <FiZap size={13} />
                      {creatingAutoPO ? 'Creating…' : `Auto-Create All HIGH Priority POs (${highRiskCount})`}
                    </button>
                  </div>
                </div>
                <div className="inv-table-wrap" style={{ border: 'none', borderRadius: '0 0 var(--inv-r-xl) var(--inv-r-xl)' }}>
                  <table className="inv-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={procurement.length > 0 && selectedRows.size === procurement.length}
                            ref={el => { if (el) el.indeterminate = selectedRows.size > 0 && selectedRows.size < procurement.length; }}
                            onChange={toggleAll}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                        <th>Material ID</th>
                        <th>Plant</th>
                        <th>Current Stock</th>
                        <th>Days Left</th>
                        <th>Priority</th>
                        <th>Reorder Qty</th>
                        <th>Action</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {procurement.length === 0 ? (
                        <tr><td colSpan={9} className="inv-table-empty">No items below warning threshold</td></tr>
                      ) : procurement.map((item, idx) => (
                        <tr
                          key={idx}
                          style={{ '--row-delay': `${idx * 40}ms` }}
                          className={item.priority === 'HIGH' ? 'inv-row-high' : 'inv-row-medium'}
                        >
                          <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedRows.has(idx)}
                              onChange={() => toggleRow(idx)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td onClick={() => openSidebar(item)} style={{ cursor: 'pointer' }}>
                            <strong>{item.material_id}</strong>
                          </td>
                          <td onClick={() => openSidebar(item)} style={{ cursor: 'pointer' }}>{item.plant}</td>
                          <td onClick={() => openSidebar(item)} style={{ cursor: 'pointer' }}>{item.current_stock?.toLocaleString()}</td>
                          <td onClick={() => openSidebar(item)} style={{ cursor: 'pointer' }}>
                            <strong style={{ color: Number(item.days_left) <= 5 ? '#991b1b' : 'inherit' }}>
                              {Number(item.days_left).toFixed(1)}
                            </strong> days
                          </td>
                          <td onClick={() => openSidebar(item)} style={{ cursor: 'pointer' }}>
                            <span className={`inv-badge ${getBadgeClass(item.priority)}`}>{item.priority}</span>
                          </td>
                          <td onClick={() => openSidebar(item)} style={{ cursor: 'pointer' }}>{item.reorder_quantity?.toLocaleString()}</td>
                          <td onClick={() => openSidebar(item)} style={{ cursor: 'pointer', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.action}>{item.action}</td>
                          <td style={{ width: 120, paddingRight: 12 }}>
                            <button
                              className="inv-btn inv-btn-row-po"
                              onClick={(e) => { e.stopPropagation(); openSidebar(item); }}
                            >
                              <FiPlusCircle size={11} /> Create PO
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </>
          )}

          {/* ════ PURCHASE ORDERS ════ */}
          {activeTab === 'orders' && (
            <div className="inv-card">
              <div className="inv-panel-hd">
                <div>
                  <h3>Purchase Orders</h3>
                  <small>{purchaseOrders.length} order{purchaseOrders.length !== 1 ? 's' : ''} total</small>
                </div>
                <button
                  className="inv-btn inv-btn-ghost"
                  onClick={() => { fetchData(); showToast('Purchase orders refreshed'); }}
                  disabled={loading}
                >
                  <FiRefreshCw size={13} className={loading ? 'inv-spinner' : ''} /> Refresh
                </button>
              </div>
              <div className="inv-table-wrap" style={{ border: 'none', borderRadius: '0 0 var(--inv-r-xl) var(--inv-r-xl)' }}>
                <table className="inv-table">
                  <thead>
                    <tr>
                      <th>PO Number</th>
                      <th>Material ID</th>
                      <th>Plant</th>
                      <th>Vendor</th>
                      <th>Quantity</th>
                      <th>PO Date</th>
                      <th>Currency</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrders.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="inv-table-empty">
                          No purchase orders yet — go to Procurement to create one
                        </td>
                      </tr>
                    ) : purchaseOrders.map((po, idx) => (
                      <tr key={idx} style={{ '--row-delay': `${idx * 40}ms` }}>
                        <td><span className="inv-po-code">{po.po_number}</span></td>
                        <td><strong>{po.material_id}</strong></td>
                        <td>{po.plant}</td>
                        <td>{po.vendor}{po.vendor_name ? ` — ${po.vendor_name}` : ''}</td>
                        <td>{po.quantity?.toLocaleString()}</td>
                        <td>{fmtFullDate(po.po_date)}</td>
                        <td>{po.currency || '—'}</td>
                        <td><span className={`inv-badge ${getBadgeClass(po.status)}`}>{po.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ════ MANUAL PO ════ */}
          {activeTab === 'manual' && (
            <div className="inv-card">
              <div className="inv-panel-hd">
                <div>
                  <h3>Manual Purchase Order</h3>
                  <small>Create a PO for any material manually</small>
                </div>
              </div>
              <div className="inv-card-body">
                <form className="inv-manual-form" onSubmit={handleManualFormSubmit}>
                  <div className="inv-field">
                    <label>Material ID</label>
                    <input
                      list="inv-mat-list-manual"
                      type="text"
                      value={manualMat}
                      onChange={e => setManualMat(e.target.value)}
                      placeholder="e.g. MAT001"
                      required
                    />
                    <datalist id="inv-mat-list-manual">
                      {materials.map(m => (
                        <option key={m.material_id} value={m.material_id}>{m.material_description}</option>
                      ))}
                    </datalist>
                  </div>
                  <div className="inv-field">
                    <label>Plant</label>
                    <input
                      type="text"
                      value={manualPlant}
                      onChange={e => setManualPlant(e.target.value)}
                      placeholder="e.g. 1000"
                      required
                    />
                  </div>
                  <div className="inv-field">
                    <label>Vendor</label>
                    <input
                      type="text"
                      value={manualVendor}
                      onChange={e => setManualVendor(e.target.value)}
                      placeholder="e.g. 600000"
                      required
                    />
                  </div>
                  <div className="inv-field">
                    <label>Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={manualQty}
                      onChange={e => setManualQty(e.target.value)}
                      placeholder="e.g. 500"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="inv-btn inv-btn-primary"
                    disabled={manualLoading}
                    style={{ height: 38, alignSelf: 'flex-end' }}
                  >
                    {manualLoading
                      ? <><span className="inv-spinner" style={{ marginRight: 4 }} /> Creating…</>
                      : <><FiPlusCircle size={13} /> Create PO</>}
                  </button>
                </form>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ════ AUTO-CREATE CONFIRM MODAL ════ */}
      {showAutoConfirm && (
        <div className="inv-review-overlay">
          <div className="inv-review-modal">
            <div className="inv-review-hd">
              <h3>Confirm Auto-Create POs</h3>
              <button className="inv-sidebar-close" onClick={() => setShowAutoConfirm(false)}>
                <FiX size={13} />
              </button>
            </div>
            <div className="inv-review-body">
              <div className="inv-info-box inv-box-warning">
                <FiAlertTriangle size={16} style={{ flexShrink: 0 }} />
                <div>
                  <strong>This will automatically create SAP Purchase Orders</strong>
                  <p>POs will be created for all <strong>{highRiskCount} HIGH</strong> and MEDIUM risk materials based on AI procurement recommendations. This action cannot be undone.</p>
                </div>
              </div>
            </div>
            <div className="inv-review-footer">
              <button className="inv-btn inv-btn-ghost" onClick={() => setShowAutoConfirm(false)}>Cancel</button>
              <button className="inv-review-confirm inv-review-confirm-danger" onClick={handleAutoCreateAll}>
                <FiZap size={14} /> Confirm Auto-Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════ ALERTS DRAWER ════ */}
      {alertDrawerOpen && alertDrawerItem && (
        <>
          <div className="inv-sidebar-overlay" onClick={closeAlertDrawer} />
          <div className="inv-sidebar">
            <div className="inv-sidebar-hd">
              <div className="inv-sidebar-hd-info">
                <span className={`inv-badge ${getBadgeClass(alertDrawerItem.risk)}`}>{alertDrawerItem.risk}</span>
                <span className="inv-sidebar-material-id">{alertDrawerItem.material_id}</span>
                {(alertDrawerItem.material_description || alertDrawerStock?.material_description) && (
                  <span className="inv-sidebar-material-name">
                    {alertDrawerItem.material_description || alertDrawerStock?.material_description}
                  </span>
                )}
              </div>
              <button className="inv-sidebar-close" onClick={closeAlertDrawer}><FiX size={13} /></button>
            </div>
            <div className="inv-sidebar-body">
              {/* Alert summary */}
              <div className="inv-sidebar-section">
                <div className="inv-sidebar-section-title">Alert Summary</div>
                <div className="inv-sidebar-rows">
                  <div className="inv-sidebar-row"><span>Plant</span><strong>{alertDrawerItem.plant}</strong></div>
                  <div className="inv-sidebar-row">
                    <span>Days Left</span>
                    <strong className={Number(alertDrawerItem.days_left) <= 5 ? 'days-critical' : Number(alertDrawerItem.days_left) <= 12 ? 'days-warn' : ''}>
                      {Number(alertDrawerItem.days_left).toFixed(1)} days
                    </strong>
                  </div>
                  <div className="inv-sidebar-row"><span>Risk Score</span><strong>{alertDrawerItem.risk_score ?? '—'}</strong></div>
                  <div className="inv-sidebar-row"><span>Stockout Date</span><strong>{fmtFullDate(alertDrawerItem.expected_stockout_date)}</strong></div>
                  <div className="inv-sidebar-row"><span>Action</span><strong style={{ color: '#991b1b' }}>{alertDrawerItem.action}</strong></div>
                </div>
              </div>

              {/* Reason */}
              {alertDrawerItem.reason && (
                <div className="inv-sidebar-section">
                  <div className="inv-sidebar-section-title">Risk Reason</div>
                  <p className="inv-sidebar-reasoning">{alertDrawerItem.reason}</p>
                </div>
              )}

              {/* Stock Prediction */}
              {alertDrawerLoading ? (
                <div className="inv-sidebar-section">
                  <div className="inv-sidebar-analysis-loading"><span className="inv-spinner" /> Loading stock data…</div>
                </div>
              ) : alertDrawerStock && (
                <>
                  <div className="inv-sidebar-section">
                    <div className="inv-sidebar-section-title">Stock Health</div>
                    {alertDrawerStock.health_score != null && (
                      <div className="inv-health-score-wrap" style={{ margin: '0 0 12px' }}>
                        <span className="inv-health-score-label">Health</span>
                        <span className="inv-health-score-num" style={{ color: getHealthColor(alertDrawerStock.health_score), fontSize: '1rem' }}>
                          {alertDrawerStock.health_score}/100
                        </span>
                        <div className="inv-health-score-bar">
                          <div className="inv-health-score-fill"
                            style={{ width: `${alertDrawerStock.health_score}%`, background: getHealthGradient(alertDrawerStock.health_score) }} />
                        </div>
                      </div>
                    )}
                    <div className="inv-sidebar-rows">
                      <div className="inv-sidebar-row"><span>Current Stock</span><strong>{alertDrawerStock.current_stock?.toLocaleString()} units</strong></div>
                      <div className="inv-sidebar-row"><span>Weekly Demand</span><strong>{alertDrawerStock.predicted_weekly_demand} units/wk</strong></div>
                      <div className="inv-sidebar-row"><span>Safety Stock</span><strong>{alertDrawerStock.safety_stock}</strong></div>
                      <div className="inv-sidebar-row"><span>Reorder Point</span><strong>{alertDrawerStock.reorder_point}</strong></div>
                    </div>
                  </div>

                  {alertDrawerStock.analysis && (
                    <div className="inv-sidebar-section">
                      <div className="inv-sidebar-section-title">AI Analysis</div>
                      <p className="inv-sidebar-analysis">{alertDrawerStock.analysis}</p>
                    </div>
                  )}
                </>
              )}

              {/* Mini forecast chart */}
              {alertForecastData.length > 0 && (
                <div className="inv-sidebar-section">
                  <div className="inv-sidebar-section-title">12-Week Forecast</div>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={alertForecastData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#d8e3f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 9, fill: '#5b7293' }} />
                      <YAxis tick={{ fontSize: 9, fill: '#5b7293' }} />
                      <Tooltip contentStyle={{ fontSize: 11, background: '#fff', border: '1px solid #d8e3f0', borderRadius: 6 }} />
                      <Line type="monotone" dataKey="upper"  stroke="#93b4e8" strokeWidth={1} dot={false} strokeDasharray="3 2" />
                      <Line type="monotone" dataKey="lower"  stroke="#93b4e8" strokeWidth={1} dot={false} strokeDasharray="3 2" />
                      <Line type="monotone" dataKey="demand" stroke="#0f4c81" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="inv-sidebar-footer">
              <button
                className="inv-sidebar-proceed"
                onClick={() => {
                  closeAlertDrawer();
                  setActiveTab('procurement');
                }}
              >
                Go to Procurement <FiChevronRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* ════ PROCUREMENT SIDEBAR ════ */}
      {sidebarOpen && selectedItem && (
        <>
          <div className="inv-sidebar-overlay" onClick={closeSidebar} />
          <div className="inv-sidebar">
            <div className="inv-sidebar-hd">
              <div className="inv-sidebar-hd-info">
                <span className={`inv-badge ${getBadgeClass(selectedItem.priority)}`}>{selectedItem.priority}</span>
                <span className="inv-sidebar-material-id">{selectedItem.material_id}</span>
                {sidebarStock?.material_description && (
                  <span className="inv-sidebar-material-name">{sidebarStock.material_description}</span>
                )}
              </div>
              <button className="inv-sidebar-close" onClick={closeSidebar}><FiX size={13} /></button>
            </div>
            <div className="inv-sidebar-body">
              <div className="inv-sidebar-section">
                <div className="inv-sidebar-section-title">Material Summary</div>
                <div className="inv-sidebar-rows">
                  <div className="inv-sidebar-row"><span>Plant</span><strong>{selectedItem.plant}</strong></div>
                  <div className="inv-sidebar-row"><span>Current Stock</span><strong>{selectedItem.current_stock?.toLocaleString()} units</strong></div>
                  <div className="inv-sidebar-row"><span>Weekly Demand</span><strong>{selectedItem.weekly_demand?.toLocaleString()} units/wk</strong></div>
                  <div className="inv-sidebar-row">
                    <span>Days Left</span>
                    <strong className={Number(selectedItem.days_left) <= 5 ? 'days-critical' : Number(selectedItem.days_left) <= 12 ? 'days-warn' : ''}>
                      {Number(selectedItem.days_left).toFixed(1)} days
                    </strong>
                  </div>
                </div>
              </div>
              <div className="inv-sidebar-section">
                <div className="inv-sidebar-section-title">AI Assessment</div>
                {sidebarLoading ? (
                  <div className="inv-sidebar-analysis-loading"><span className="inv-spinner" /> Loading analysis…</div>
                ) : sidebarStock?.analysis ? (
                  <p className="inv-sidebar-analysis">{sidebarStock.analysis}</p>
                ) : (
                  <p className="inv-sidebar-analysis" style={{ opacity: 0.5, fontStyle: 'italic' }}>No AI analysis available.</p>
                )}
              </div>
              <div className="inv-sidebar-section">
                <div className="inv-sidebar-section-title">Recommendation</div>
                <div className="inv-sidebar-reorder">
                  <span>Suggested Reorder Qty</span>
                  <strong>{selectedItem.reorder_quantity?.toLocaleString()}</strong>
                </div>
                {selectedItem.reasoning && (
                  <p className="inv-sidebar-reasoning">{selectedItem.reasoning}</p>
                )}
              </div>
            </div>
            <div className="inv-sidebar-footer">
              <button className="inv-sidebar-proceed" onClick={openReview}>
                Proceed to Create PO <FiChevronRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* ════ PO REVIEW MODAL ════ */}
      {showReview && selectedItem && (
        <div className="inv-review-overlay">
          <div className="inv-review-modal">
            <div className="inv-review-hd">
              <h3>Review Purchase Order</h3>
              <button className="inv-sidebar-close" onClick={() => setShowReview(false)}><FiX size={13} /></button>
            </div>
            <div className="inv-review-body">
              <div className="inv-review-field">
                <label>Material ID</label>
                <div className="inv-review-locked">{selectedItem.material_id}</div>
              </div>
              {sidebarStock?.material_description && (
                <div className="inv-review-field">
                  <label>Material Name</label>
                  <div className="inv-review-locked">{sidebarStock.material_description}</div>
                </div>
              )}
              <div className="inv-review-field">
                <label>Plant</label>
                <div className="inv-review-locked">{selectedItem.plant}</div>
              </div>
              <div className="inv-review-field">
                <label>Vendor</label>
                <input className="inv-review-input" value={reviewVendor} onChange={e => setReviewVendor(e.target.value)} placeholder="e.g. 600000" />
              </div>
              <div className="inv-review-field">
                <label>Quantity</label>
                <input className="inv-review-input" type="number" min="1" value={reviewQty} onChange={e => setReviewQty(e.target.value)} />
              </div>
              {poResult && (
                <div className={`inv-info-box inv-review-result ${poResult.status === 'CREATED' ? 'inv-box-success' : 'inv-box-warning'}`}>
                  <FiCheckCircle size={16} style={{ flexShrink: 0 }} />
                  <div>
                    <strong>PO {poResult.po_number} — {poResult.status}</strong>
                    <p>Qty: {poResult.quantity?.toLocaleString()} · Vendor: {poResult.vendor}{poResult.vendor_name ? ` — ${poResult.vendor_name}` : ''}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="inv-review-footer">
              <button className="inv-btn inv-btn-ghost" onClick={() => setShowReview(false)}>Cancel</button>
              <button
                className="inv-review-confirm"
                onClick={handleCreateManualPO}
                disabled={creatingManualPO || !reviewVendor.trim() || !reviewQty || poResult}
              >
                {creatingManualPO ? <><span className="inv-spinner" /> Creating…</> : 'Confirm & Create PO'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
